import { Box, Backdrop, Button, Grid, styled, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";
import { addTraining, useAddTraining } from "../../../../api/effects/mutations/Training/addTraining";
import { useEditTraining } from "../../../../api/effects/mutations/Training/editTraining";
import { useFetchTraining } from "../../../../api/effects/mutations/Training/useFetchTraining";
import InputDate from "../../../../components/InputDate";
import InputRadio from "../../../../components/InputRadio";
import InputTextField from "../../../../components/InputTextField";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { formatDate, convertDate, toIsoStringDate } from "../../../../utils/dateConvert";
import InputDateForm from "../../../../components/InputDateForm";
import { InputTextFieldForm } from "../../../../components/InputTextFieldForm";
import { useForm, Controller, SubmitHandler } from "react-hook-form";
const SquareGrid = styled(Grid)(({ theme }) => ({
  backgroundColor: "#FFF",
  height: "auto",
  width: "90%",
  boxShadow: "1px 1px 10px #888888",
  borderRadius: "10px",
  padding: "1%",
  [theme.breakpoints.down("sm")]: {
    padding: "10px",
  },
  margin: "1%",
}));

const FullButton = styled(Button)(({ theme }) => ({
  width: "70%",
}));

const ScrollGrid = styled(Grid)(({ theme }) => ({
  overflow: "auto",
  maxHeight: "90vh",
}));

type Props = {
  open: boolean;
  handleClose: () => void;
  ID?: number;
};
type FormValues = {
  training: string;
  penyelengara: string;
  kota: string;
  tahunMulai: string;
  tahunSelesai: string;
  pembiayaan: string;
  lulus: boolean;
};
const schema = z.object({
  training: z.string().min(1, "Training field is required"),
  penyelengara: z.string().min(1, "Penyelengara  field is required"),
  kota: z.string().min(1, "Kota field is required"),
  //  Tahun Mulai Training
  tahunMulai: z.string({ required_error: "Tanggal Mulai field is required" })
    .min(1, "Tanggal Mulai field is required").refine(val => {
      const date = new Date(val);
      return !isNaN(date.getTime()); // Check if the date is valid
    }, { message: "Invalid date format" })
    .refine(val => {
      const date = new Date(val);
      const minDate = new Date(1900, 0, 1);
      const maxDate = new Date();
      return date >= minDate && date <= maxDate; // Check if the date is within the valid range
    }, { message: "Date must be between January 1, 1900 and today" }),
  // Tahun Selesai training
  tahunSelesai: z.string({ required_error: "Tanggal Selesai field is required" })
    .min(1, "Tanggal Selesai is required").refine(val => {
      const date = new Date(val);
      return !isNaN(date.getTime()); // Check if the date is valid
    }, { message: "Invalid date format" })
    .refine(val => {
      const date = new Date(val);
      const minDate = new Date(1900, 0, 1);
      const maxDate = new Date();
      return date >= minDate && date <= maxDate; // Check if the date is within the valid range
    }, { message: "Date must be between January 1, 1900 and today" }),
  pembiayaan: z.string().min(1, "Pembiayaan  field is required"),
  lulus: z.boolean(),
}).refine((data) => {
  if (data.tahunMulai && data.tahunSelesai) {
    return new Date(data.tahunSelesai) >= new Date(data.tahunMulai);
  }
  return true;
}, {
  message: "End Date Can't be before Start Date",
  path: ["tahunSelesai"],
}
);

export default function TrainingModal({ ID = -1, open, handleClose }: Props) {
  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
  } = useForm<FormValues>({
    mode: "onSubmit",
    resolver: zodResolver(schema),
    defaultValues: {
      training: "",
      penyelengara: "",
      kota: "",
      pembiayaan: "",
      tahunMulai: "",
      tahunSelesai: "",
      lulus: false

    },
  });

  const { mutateAsync: GetTrainingData } = useFetchTraining({
    onSuccess(data) {
      setValue("training", data.TRAINING)
      setValue("penyelengara", data.PENYELENGARA)
      setValue("kota", data.KOTA)
      setValue("tahunMulai", formatDate(data.TAHUN_MULAI))
      setValue("tahunSelesai", formatDate(data.TAHUN_SELESAI))
      setValue("pembiayaan", data.PEMBIAYAAN);
      setValue("lulus", data.LULUS)

      console.log("Success");
    },
    onError() {
      console.log("Error");
    },
  });

  useEffect(() => {
    if (ID > 0) {
      GetTrainingData(ID);
    }
  }, [open]);

  const handleModalClose = () => {
    reset();
    handleClose();
  };
  useEffect(() => {
    if (!open) {
      reset();
    }
  }, [open, reset]);


  const { mutateAsync: AddTraining, isLoading: isAdding } = useAddTraining({
    onSuccess() {
      handleModalClose();
      console.log("Success");
    },
    onError() {
      console.log("Error");
    },
  });

  const { mutateAsync: EditTraining, isLoading: isEditing } = useEditTraining({
    onSuccess() {
      handleModalClose();
      console.log("Success");
    },
    onError() {
      console.log("Error");
    },
  });

  const onSubmit: SubmitHandler<FormValues> = async (data) => {
    const payload = {
      TRAINING: data.training,
      PENYELENGARA: data.penyelengara,
      KOTA: data.kota,
      TAHUN_MULAI: toIsoStringDate(data.tahunMulai),
      TAHUN_SELESAI: toIsoStringDate(data.tahunSelesai),
      PEMBIAYAAN: data.pembiayaan,
      LULUS: data.lulus,
    };
    try {
      if (ID > 0) {
        await EditTraining({
          TRAINING_ID: ID,
          ...payload,
        });
      } else {
        await AddTraining(payload);
      }
    } catch (error) {
      console.error("Submission failed:", error);
    }
  };
  return (
    <Backdrop
      sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
      open={open}
    >
      <Box component={"form"} onSubmit={handleSubmit(onSubmit)} noValidate>
        <SquareGrid container>
          <ScrollGrid container>
            <Grid container justifyContent="flex-start" paddingLeft={"10.5%"} item xs={6}
              paddingBottom={
                {
                  xs: 5,
                  sm: 5,
                  md: 5,
                  lg: 5,
                  xl: 5
                }}>
              <Typography variant="subtitle1" color="black">
                {ID === -1
                  ? "Tambah Pengalaman Training"
                  : "Ubah Pengalaman Training"}
              </Typography>
            </Grid>
            <Grid container justifyContent="flex-end" item xs={6}>
              <Button onClick={handleClose}>Close</Button>
            </Grid>
            {/* Training - Text Field */}
            <InputTextFieldForm
              name="training"
              control={control}
              label="Training"
              error={errors.training}
            />
            {/* Penyelengara - Text Field */}
            <InputTextFieldForm
              name="penyelengara"
              control={control}
              label="Penyelengara"
              error={errors.penyelengara}
            />

            {/* Kota - Text Field */}
            <InputTextFieldForm
              name="kota"
              control={control}
              label="Kota"
              error={errors.kota}
            />
            {/* Tahun Mulai - Date */}
            <InputDateForm
              name="tahunMulai"
              control={control}
              label="Tanggal Mulai"
              error={errors.tahunMulai}
              disabled={false}
            />
            {/* Tahun Selesai - Date */}
            <InputDateForm
              name="tahunSelesai"
              control={control}
              label="Tanggal Selesai"
              error={errors.tahunSelesai}
              disabled={false}
            />
            {/* Pembiayaan - Text Field */}
            <InputTextFieldForm
              name="pembiayaan"
              control={control}
              label="Pembiayaan"
              error={errors.pembiayaan}
            />
            {/* Lulus - Radio */}
            <Grid container justifyContent="center" item xs={12}>
              <Controller
                name="lulus"
                control={control}
                render={({ field }) => (
                  <InputRadio
                    title={"Lulus"}
                    value={field.value}
                    handleEdit={field.onChange}
                  />
                )}
              />
            </Grid>
            <Grid container justifyContent="center" item xs={12}
              paddingTop={{
                xs: 2,
                sm: 2,
                md: 2,
                lg: 2,
                xl: 2
              }}
              paddingBottom={{
                xs: 2,
                sm: 2,
                md: 2,
                lg: 2,
                xl: 2
              }}>
              <FullButton variant="contained" type="submit" disabled={isAdding || isEditing}>
                {(isAdding || isEditing) ? "Saving..." : "Save"}
              </FullButton>
            </Grid>
          </ScrollGrid>
        </SquareGrid>
      </Box>

    </Backdrop>
  );
}
